import { Role } from '@jaam/commons/constants';
import { CompanyDto } from '@jaam/commons/dtos';

/** Compagnie (ex: activeCompany de getMyInfo) avec rôles sur l'objet. */
export type CompanyWithRoles = {
  roles?: { name?: string }[];
} | null | undefined;

/**
 * Récupère les rôles de l'utilisateur pour cette compagnie.
 * Supporte company.roles (API courante) ou company.userCompanies?.[0]?.roles.
 */
export function getRolesFromCompany(company?: CompanyDto | null): { name?: string }[] {
  const userCompany = company?.userCompanies?.[0];
  return userCompany?.roles ?? company?.roles ?? [];
}

/**
 * Retourne le premier nom de rôle pour la compagnie (ex: "Admin", "Staff").
 */
export function getRoleFromCompany(company?: CompanyDto | null): string | undefined {
  const roles = getRolesFromCompany(company);
  if (roles.length === 0) return undefined;
  return roles[0]?.name;
}

/**
 * Vérifie si l'utilisateur a le rôle donné pour cette compagnie.
 */
export function hasRole(company?: CompanyDto | null, roleName?: string): boolean {
  if (!roleName) return false;
  const roles = getRolesFromCompany(company);
  return roles.some(
    (role) => role.name?.toLowerCase() === roleName.toLowerCase()
  );
}

/**
 * Indique si l'utilisateur a le rôle Admin pour cette compagnie.
 */
export function isAdmin(company?: CompanyDto | null): boolean {
  return hasRole(company, Role.Admin);
}

/**
 * Indique si l'utilisateur a le rôle Accountant pour cette compagnie.
 */
export function isAccountant(company?: CompanyDto | null): boolean {
  return hasRole(company, Role.Accountant);
}

/**
 * Vérifie si l'utilisateur a au moins un des rôles donnés.
 */
export function hasAnyRole(
  company?: CompanyDto | null,
  roleNames: string[] = []
): boolean {
  if (roleNames.length === 0) return false;
  const roles = getRolesFromCompany(company);
  return roleNames.some((roleName) =>
    roles.some(
      (role) => role?.name?.toLowerCase() === roleName.toLowerCase()
    )
  );
}

/**
 * Indique si la compagnie a des données de rôles (au moins un rôle).
 */
export function hasRoleData(company?: CompanyDto | null): boolean {
  return getRolesFromCompany(company).length > 0;
}

/**
 * Indique si l'utilisateur a le rôle Admin pour cette compagnie.
 * S'appuie sur activeCompany.roles (ex: [{ id: 3, name: "Staff" }] ou [{ name: "Admin" }]).
 * Utilisé par les middlewares (auth-web, payrollr-web, edeclaration-web) pour décider
 * si on redirige vers /company/plan : seuls les admins doivent configurer le plan.
 */
export function isAdminForActiveCompany(company: CompanyWithRoles): boolean {
  const roles = company?.roles ?? [];
  return roles.some(
    (r) => r?.name?.toLowerCase() === Role.Admin.toLowerCase()
  );
}
